/* eslint-disable react/prop-types */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import TableShapeIllustration from "./TableShapeIllustration";
import "./ReservationTablePicker.css";

const SHAPE_ORDER = ["bar", "booth", "round", "square", "rectangle", "oval"];

const SHAPE_LABEL_KEYS = {
  round: "reservationShapeRound",
  square: "reservationShapeSquare",
  rectangle: "reservationShapeRectangle",
  oval: "reservationShapeOval",
  booth: "reservationShapeBooth",
  bar: "reservationShapeBar",
};

const inferTableShape = (table) => {
  if (table?.shape && SHAPE_LABEL_KEYS[table.shape]) {
    return table.shape;
  }
  const name = String(table?.name || "").toLowerCase();
  const match = SHAPE_ORDER.find((shape) => name.startsWith(shape));
  return match || "round";
};

const getTableLabel = (table, t) =>
  table?.name || `${t("reservationTable")} ${table?.tableNumber || ""}`.trim();

export default function ReservationTablePicker({
  value = "",
  options = [],
  onChange,
  disabled = false,
  unassigned = false,
  variant = "inline",
  ariaLabel,
  tableColor,
}) {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});

  const selectedId = value ? String(value) : "";
  const selectedEntry = options.find(({ table }) => String(table._id) === selectedId);
  const selectedTable = selectedEntry?.table;

  const groupedOptions = useMemo(() => {
    const buckets = Object.fromEntries(SHAPE_ORDER.map((shape) => [shape, []]));

    options.forEach((entry) => {
      const shape = inferTableShape(entry.table);
      if (buckets[shape]) {
        buckets[shape].push(entry);
      }
    });

    return SHAPE_ORDER.map((shape) => ({
      shape,
      labelKey: SHAPE_LABEL_KEYS[shape],
      items: buckets[shape],
    })).filter((group) => group.items.length > 0);
  }, [options]);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuWidth = variant === "form" ? Math.max(rect.width, 300) : 320;
    const menuMaxHeight = 380;
    const gap = 6;

    let top = rect.bottom + gap;
    let left = rect.left;

    if (left + menuWidth > window.innerWidth - 10) {
      left = Math.max(10, window.innerWidth - menuWidth - 10);
    }

    if (top + menuMaxHeight > window.innerHeight - 10) {
      top = Math.max(10, rect.top - menuMaxHeight - gap);
    }

    setMenuStyle({
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      width: `${menuWidth}px`,
      maxHeight: `${menuMaxHeight}px`,
      zIndex: 10050,
    });
  }, [variant]);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
  }, [open, updateMenuPosition, groupedOptions.length]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        rootRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    const handleReposition = () => updateMenuPosition();

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open, updateMenuPosition]);

  const handleSelect = (nextValue, table) => {
    const nextId = nextValue ? String(nextValue) : "";
    if (nextId === selectedId) {
      setOpen(false);
      return;
    }
    onChange(nextId, table ? getTableLabel(table, t) : t("reservationTableUnassigned"));
    setOpen(false);
  };

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          className="reservation-table-picker__menu"
          style={menuStyle}
          role="listbox"
          aria-label={ariaLabel || t("reservationTable")}
        >
          <button
            type="button"
            role="option"
            aria-selected={!selectedId}
            className={`reservation-table-picker__option is-unassigned${
              !selectedId ? " is-selected" : ""
            }`}
            onClick={() => handleSelect("", null)}
          >
            <span className="reservation-table-picker__option-preview is-empty">
              <i className="fas fa-chair" aria-hidden />
            </span>
            <span className="reservation-table-picker__option-body">
              <strong>{t("reservationTableUnassigned")}</strong>
              <span className="reservation-table-picker__option-meta">
                {t("reservationTableUnassignedHint")}
              </span>
            </span>
          </button>

          {groupedOptions.map((group) => (
            <section
              key={group.shape}
              className={`reservation-table-picker__group is-${group.shape}`}
            >
              <header className="reservation-table-picker__group-head">
                <span className="reservation-table-picker__group-icon" aria-hidden>
                  <TableShapeIllustration
                    shape={group.shape}
                    seats={group.items[0]?.table?.seats || 4}
                    status="available"
                    baseColor={
                      group.items[0]?.table?.color || tableColor
                    }
                  />
                </span>
                <span>{t(group.labelKey)}</span>
                <em>{group.items.length}</em>
              </header>

              <div className="reservation-table-picker__group-list">
                {group.items.map(({ table, occupied }) => {
                  const shape = inferTableShape(table);
                  const isSelected = String(table._id) === selectedId;
                  const label = getTableLabel(table, t);

                  return (
                    <button
                      key={table._id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={occupied}
                      className={`reservation-table-picker__option is-${shape}${
                        isSelected ? " is-selected" : ""
                      }${occupied ? " is-booked" : ""}`}
                      onClick={() => handleSelect(table._id, table)}
                    >
                      <span className="reservation-table-picker__option-preview">
                        <TableShapeIllustration
                          shape={shape}
                          seats={table.seats}
                          status={occupied ? "booked" : "available"}
                          baseColor={table.color || tableColor}
                        />
                      </span>
                      <span className="reservation-table-picker__option-body">
                        <strong>{label}</strong>
                        <span className="reservation-table-picker__option-meta">
                          {table.seats ? (
                            <span className="reservation-table-picker__seats-pill">
                              {table.seats} {t("reservationSeatsShort")}
                            </span>
                          ) : null}
                          {occupied ? (
                            <span className="reservation-table-picker__booked-pill">
                              {t("reservationAvailability_booked")}
                            </span>
                          ) : null}
                        </span>
                      </span>
                      {isSelected ? (
                        <i className="fas fa-check reservation-table-picker__check" aria-hidden />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>,
        document.body
      )
    : null;

  const selectedShape = selectedTable ? inferTableShape(selectedTable) : null;

  return (
    <>
      <div
        ref={rootRef}
        className={`reservation-table-picker is-${variant}${
          unassigned ? " is-warn" : ""
        }${open ? " is-open" : ""}${disabled ? " is-disabled" : ""}`}
      >
        <button
          ref={triggerRef}
          type="button"
          className="reservation-table-picker__trigger"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel || t("reservationTable")}
          onClick={() => {
            if (disabled) return;
            setOpen((current) => !current);
          }}
        >
          {selectedTable ? (
            <>
              <span
                className={`reservation-table-picker__trigger-preview is-${selectedShape}`}
              >
                <TableShapeIllustration
                  shape={selectedShape}
                  seats={selectedTable.seats}
                  status="available"
                  baseColor={selectedTable.color || tableColor}
                />
              </span>
              <span
                className="reservation-table-picker__trigger-name"
                title={getTableLabel(selectedTable, t)}
              >
                {getTableLabel(selectedTable, t)}
              </span>
              <span
                className={`reservation-table-picker__trigger-seats${
                  selectedTable.seats ? "" : " is-empty"
                }`}
                aria-hidden={!selectedTable.seats}
              >
                {selectedTable.seats || ""}
              </span>
            </>
          ) : (
            <>
              <span className="reservation-table-picker__trigger-icon">
                <i className="fas fa-chair" aria-hidden />
              </span>
              <span className="reservation-table-picker__trigger-label is-muted is-span">
                {t("reservationTableUnassigned")}
              </span>
            </>
          )}
          <i className="fas fa-chevron-down reservation-table-picker__chevron" aria-hidden />
        </button>
      </div>
      {menu}
    </>
  );
}
